// 底部 Tab 栏组件
// src/components/tab-bar/index.tsx

'use client';

import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

import cls from 'classnames';

import DiscoverActiveIcon from '@/assets/svg/discover-active.svg';
import DiscoverIcon from '@/assets/svg/discover.svg';
import MyListActiveIcon from '@/assets/svg/my-list-active.svg';
import MyListIcon from '@/assets/svg/my-list.svg';
import ProfileActiveIcon from '@/assets/svg/profile-active.svg';
import ProfileIcon from '@/assets/svg/profile.svg';
import { ROUTER } from '@/constants';
import { useDebounce } from '@/hooks';
import { ETrackerEvents, trackCustom } from '@/utils/tracker';

/** Tab 埋点上报名 与 BI 口径对齐，独立于展示 key */
type TTabTrackName = 'my_list' | 'discover' | 'profile';

/** TabBar 命名空间下的 i18n key 联合 */
type TTabLabelKey = 'tab_my_list' | 'tab_discover' | 'tab_profile';

/** Tab 项配置 */
interface ITabItem {
    /** React 列表 key 也用于在 DOM/调试中识别 tab */
    key: string;
    /** 埋点上报名 与 BI 口径绑定，重命名展示 key 时不会污染报表 */
    trackName: TTabTrackName;
    /** i18n key，由 useTranslations('TabBar') 解析为展示文案 */
    labelKey: TTabLabelKey;
    /** 路由路径，与 usePathname 比对以高亮当前项 */
    path: string;
    /** 未激活态图标组件 */
    Icon: React.FC<React.SVGProps<SVGSVGElement>>;
    /** 激活态图标组件 */
    ActiveIcon: React.FC<React.SVGProps<SVGSVGElement>>;
}

/** Tab 项静态配置 文案在 render 时由 i18n 函数生成，避免组件内每次 render 重建数组 */
const TAB_ITEMS: ITabItem[] = [
    {
        key: 'My_list',
        trackName: 'my_list',
        labelKey: 'tab_my_list',
        path: ROUTER.MY_LIST,
        Icon: MyListIcon,
        ActiveIcon: MyListActiveIcon,
    },
    {
        key: 'Discover',
        trackName: 'discover',
        labelKey: 'tab_discover',
        path: ROUTER.DISCOVER,
        Icon: DiscoverIcon,
        ActiveIcon: DiscoverActiveIcon,
    },
    {
        key: 'Profile',
        trackName: 'profile',
        labelKey: 'tab_profile',
        path: ROUTER.PROFILE,
        Icon: ProfileIcon,
        ActiveIcon: ProfileActiveIcon,
    },
];

/** Tab 点击埋点防抖间隔，避免快速连点重复上报 */
const TAB_CLICK_DEBOUNCE_MS = 700;

/** 上报 Tab 点击埋点 */
function reportTabClick(tab: ITabItem): void {
    trackCustom(ETrackerEvents.TAB_CLICK, {
        tab_name: tab.trackName,
    });
}

/**
 * 底部 Tab 栏：固定在视口底部，供 (tabs) 布局使用。
 * 高度含 safe-area-inset-bottom，与 TabsLayout 的 pb 占位保持一致。
 */
export default function TabBar() {
    // i18n 文案
    const t = useTranslations('TabBar');

    // 当前路由路径 用于匹配高亮当前 tab
    const pathname = usePathname();

    // tab 点击埋点（防抖）leading：首次点击立即上报；trailing：防抖窗口内不再补发
    const handleTabClick = useDebounce(reportTabClick, TAB_CLICK_DEBOUNCE_MS, {
        leading: true,
        trailing: false,
    });

    return (
        // 固定吸底容器 高度含 safe-area-inset-bottom 适配全面屏
        <div className='fixed inset-x-0 bottom-0 z-[100] flex h-[calc(52px+env(safe-area-inset-bottom,0px))] flex-col items-center justify-start border-t border-[#f0f0f0] bg-white'>
            {/* tab 行：固定 375px 宽，等分排列 */}
            <div className='flex h-[52px] w-[375px] justify-center gap-[2px]'>
                {TAB_ITEMS.map(tab => {
                    const isActive = pathname === tab.path;
                    const IconComp = isActive ? tab.ActiveIcon : tab.Icon;
                    return (
                        // 单个 tab 项 使用 Link prefetch 提升切换体验
                        <Link
                            key={tab.key}
                            href={tab.path}
                            prefetch
                            className='flex flex-1 cursor-pointer flex-col items-center justify-center gap-[2px] text-inherit no-underline'
                            aria-current={isActive ? 'page' : undefined}
                            onClick={() => handleTabClick(tab)}
                        >
                            {/* 图标 装饰性 旁有文案承担可读名称 故 aria-hidden 避免屏幕阅读器重复读 */}
                            <div className='size-[24px]'>
                                <IconComp aria-hidden />
                            </div>

                            {/* 文案 激活态紫色加粗 / 未激活灰色 */}
                            <span
                                className={cls('text-[10px] leading-[14px]', {
                                    'font-semibold text-[#7c3aed]': isActive,
                                    'text-[#999]': !isActive,
                                })}
                            >
                                {t(tab.labelKey)}
                            </span>
                        </Link>
                    );
                })}
            </div>
        </div>
    );
}
